// src/components/EbayWidget.js
import React, { useState, useEffect } from 'react';
import { fetchEbayListings } from '../services/ebayService';

/**
 * Component to display eBay listings for a board game using eBay Browse API
 * @param {Object} props
 * @param {string} props.gameTitle - The title of the game to search for
 */
const EbayWidget = ({ gameTitle }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [listings, setListings] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Format the game title for eBay search
  const formattedTitle = encodeURIComponent(`${gameTitle} board game`);
  
  // eBay affiliate ID - replace with your actual eBay affiliate ID
  const affiliateId = "5339104460";
  
  // Direct link to eBay search results
  const ebaySearchUrl = `https://www.ebay.com/sch/i.html?_nkw=${formattedTitle}&campid=${affiliateId}`;

  useEffect(() => {
    // Only fetch listings when the component is expanded to save API calls
    if (!isExpanded) return;
    
    const getListings = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const ebayListings = await fetchEbayListings(gameTitle);
        setListings(ebayListings);
      } catch (err) {
        console.error('Error fetching eBay listings:', err);
        setError('Failed to load eBay listings. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    getListings();
  }, [gameTitle, isExpanded]);

  // Format price for display
  const formatPrice = (price) => {
    if (!price) return 'Price not available';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: price.currency || 'USD',
    }).format(price.value);
  };

  return (
    <div className="mt-4">
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="/media/ebay-logo.png" 
              alt="eBay" 
              className="h-6 mr-2"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='30' viewBox='0 0 60 30'%3E%3Crect width='60' height='30' fill='%23ffffff'/%3E%3Ctext x='30' y='15' font-family='Arial' font-size='12' fill='%23e53238' text-anchor='middle' dominant-baseline='middle'%3EeBay%3C/text%3E%3C/svg%3E";
              }}
            />
            <span className="text-gray-300">Find this game on eBay</span>
          </div>
          
          <div className="flex space-x-2">
            <a 
              href={ebaySearchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white rounded-md text-sm transition-colors"
            >
              View on eBay
            </a>
            
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-400 hover:text-gray-200"
            >
              {isExpanded ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {isExpanded && (
          <div className="mt-4">
            {isLoading ? (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-300"></div>
              </div>
            ) : error ? (
              <div className="text-red-400 text-sm py-2">
                {error}
              </div>
            ) : listings.length > 0 ? (
              <>
                <div className="space-y-3">
                  {listings.map(listing => (
                    <a 
                      key={listing.itemId}
                      href={listing.itemAffiliateWebUrl || listing.itemWebUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-2 rounded-lg hover:bg-gray-700 transition-colors"
                    >
                      <img 
                        src={listing.image?.imageUrl || '/api/placeholder/80/80'} 
                        alt={listing.title}
                        className="w-16 h-16 object-contain bg-gray-700 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <p className="text-gray-200 text-sm">{listing.title}</p>
                        <div className="flex justify-between mt-1">
                          <span className="text-gray-100 font-medium">
                            {formatPrice(listing.price)}
                          </span>
                          <span className="text-gray-400 text-xs">
                            {listing.shippingOptions && listing.shippingOptions[0]?.shippingCost?.value === 0 
                              ? 'Free shipping' 
                              : listing.shippingOptions && listing.shippingOptions[0]
                                ? `+${formatPrice(listing.shippingOptions[0].shippingCost)} shipping`
                                : ''}
                          </span>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
                
                <div className="mt-3 text-center">
                  <a 
                    href={ebaySearchUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-400 hover:text-blue-300 text-sm"
                  >
                    View more listings on eBay
                  </a>
                </div>
              </>
            ) : (
              <div className="text-gray-400 text-sm py-2 text-center">
                No eBay listings found for this game.
              </div>
            )}
            
            <div className="mt-3 text-xs text-gray-400 border-t border-gray-700 pt-2">
              <p>Prices and availability may vary. As an eBay Partner, we earn from qualifying purchases.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EbayWidget;